import React, {useEffect, useState} from "react"
import {Box, Drawer} from "@mui/material"
// Layout pieces
import PackagingSidebar from "./PackagingSidebar"
import PackagingTopbar from "./PackagingTopbar"
import SidebarTour from "./SidebarTour"
import TourWelcomeDialog from "./TourWelcomeDialog"
import {PackagingSection} from "./types"
import {packagingTokens as tk} from "@/theme/packagingTokens"
import {useTour} from "@/context/TourProvider"
import useIframeFloorHeight from "@/utils/useIframeFloorHeight"

type LayoutProps = {
    section: PackagingSection
    onSectionChange: (section: PackagingSection) => void
    organisationFilter: string
    setOrganisationFilter: (value: string) => void
    currentCrumb: string
    docLink?: string
    // Local-dev only: opens the first-visit stepper preview (top bar button).
    onDevFirstVisit?: () => void
    children: React.ReactNode
}

// Breakpoint below which the rail collapses into a drawer (keeps the wide-viewport
// look untouched; only narrow screens switch to the mobile layout).
const MOBILE = "md"

// ── TEMPORARY auto-tour (v3 launch) ──────────────────────────────────────────
// Until 2026-09-01 the guided tour opens automatically, once per browser, for
// EVERY user (not just backend first-timers) when they land on the dashboard
// section. It can never overlap the first-visit stepper: Main doesn't mount
// this layout while the stepper is shown. After the deadline the trigger goes
// inert — this block is then DEAD CODE and can be deleted.
const AUTO_TOUR_DEADLINE = new Date("2026-09-01T00:00:00")
const AUTO_TOUR_SEEN_KEY = "pkg_v3_tour_auto_shown"

// The packaging-v3 application shell. On wide viewports it's a fixed-width sidebar
// rail + content column. Below `md` the rail collapses into a temporary Drawer
// opened from the top bar's hamburger, and the content goes full-width.
//
// The app is embedded as a *content-sized* iframe, so the shell is a plain flex
// row that grows with its content — no `position: fixed` / `100vh`.
export default function PackagingLayout(props: LayoutProps) {
    const {
        section,
        onSectionChange,
        organisationFilter,
        setOrganisationFilter,
        currentCrumb,
        docLink,
        onDevFirstVisit,
        children,
    } = props

    const [drawerOpen, setDrawerOpen] = useState(false)
    const floorHeight = useIframeFloorHeight()

    // Optional guided tour. Auto-starts once for users who haven't seen it
    // (driven by TourProvider's `homeTour` / backend "done" flag), and can be
    // re-launched any time from the top bar's Tour button.
    const tour = useTour?.()
    const [tourOpen, setTourOpen] = useState(false)
    // Every tour trigger (manual button, first-visit flag, v3 auto-tour) goes
    // through this welcome prompt first — the tour never starts unasked.
    const [tourPrompt, setTourPrompt] = useState(false)

    useEffect(() => {
        if (tour?.homeTour === 1) setTourPrompt(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tour?.homeTour])

    // TEMPORARY auto-tour — see AUTO_TOUR_DEADLINE above (dead code after 2026-09-01).
    useEffect(() => {
        if (section !== "dashboard") return
        if (new Date() >= AUTO_TOUR_DEADLINE) return // expired — trigger is inert
        try {
            if (localStorage.getItem(AUTO_TOUR_SEEN_KEY)) return // already auto-shown on this browser
            localStorage.setItem(AUTO_TOUR_SEEN_KEY, new Date().toISOString())
        } catch {
            return // storage unavailable (e.g. blocked third-party iframe) — skip rather than loop
        }
        setTourPrompt(true)
    }, [section])

    const startTour = () => {
        setTourPrompt(false)
        setTourOpen(true)
    }

    // Declining the prompt has the same semantics as closing the tour: a
    // backend first-visit flag is consumed so the user isn't re-prompted.
    const declineTour = () => {
        setTourPrompt(false)
        if (tour?.homeTour === 1) {
            tour?.setHomeTour?.(0)
            tour?.handleEndTour?.()
        }
    }

    const closeTour = () => {
        setTourOpen(false)
        if (tour?.homeTour === 1) {
            tour?.setHomeTour?.(0)
            tour?.handleEndTour?.()
        }
    }

    // On mobile, selecting a section also closes the drawer.
    const handleSection = (s: PackagingSection) => {
        onSectionChange(s)
        setDrawerOpen(false)
    }

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "stretch",
                // Fill the iframe floor (so the rail reaches the footer) without the
                // 100vh ratchet — see useIframeFloorHeight.
                minHeight: floorHeight,
                width: "100%",
                maxWidth: "100%",
                overflowX: "hidden",
                boxSizing: "border-box",
                backgroundColor: tk.color.bg,
            }}
        >
            {/* Permanent rail — wide viewports only */}
            <PackagingSidebar
                section={section}
                onSectionChange={handleSection}
                organisationFilter={organisationFilter}
                setOrganisationFilter={setOrganisationFilter}
                sx={{display: {xs: "none", [MOBILE]: "flex"}}}
            />

            {/* Mobile drawer rail */}
            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                sx={{display: {xs: "block", [MOBILE]: "none"}, "& .MuiDrawer-paper": {border: "none"}}}
            >
                <PackagingSidebar
                    section={section}
                    onSectionChange={handleSection}
                    organisationFilter={organisationFilter}
                    setOrganisationFilter={setOrganisationFilter}
                />
            </Drawer>

            <Box sx={{flex: 1, minWidth: 0, display: "flex", flexDirection: "column"}}>
                <PackagingTopbar
                    // On the dashboard section "Packaging" itself closes the
                    // trail; elsewhere the section label is appended and
                    // "Packaging" links back to the dashboard.
                    currentCrumb={section === "dashboard" ? undefined : currentCrumb}
                    onPackagingHome={() => handleSection("dashboard")}
                    docLink={docLink}
                    onStartTour={() => setTourPrompt(true)}
                    onMenuClick={() => setDrawerOpen(true)}
                    onDevFirstVisit={onDevFirstVisit}
                />
                <Box
                    sx={{
                        flex: 1,
                        p: {xs: 2, sm: `${tk.layout.contentPadding}px`},
                        maxWidth: tk.layout.contentMaxWidth,
                        animation: "packagingFadeUp .4s ease both",
                        "@keyframes packagingFadeUp": {
                            from: {opacity: 0, transform: "translateY(8px)"},
                            to: {opacity: 1, transform: "translateY(0)"},
                        },
                    }}
                >
                    {children}
                </Box>
            </Box>

            <TourWelcomeDialog open={tourPrompt} onStart={startTour} onClose={declineTour} />
            <SidebarTour open={tourOpen} onClose={closeTour} section={section} onSectionChange={handleSection} />
        </Box>
    )
}
